import { ChangeEvent, FunctionComponent } from "react";
import { UseFormSetValue } from "react-hook-form";

import { useTranslation } from "react-i18next";

import { Checkbox } from "@/components/Checkbox/Checkbox";
import { Information } from "@/components/Information/Information";
import { InputField } from "@/components/InputField/InputField";
import { SelectField } from "@/components/SelectField/SelectField";

import { NewInRoleChangeType } from "@/enum/company-enrich-type.enum";

import {
	NEW_IN_ROLE_CHANGE_TYPES,
	NEW_IN_ROLE_WINDOW_PRESETS,
	NewInRoleWindowPreset,
} from "../constants/newInRole.constants";
import { CreateCompanyEnrich, NewInRole } from "../schema/companyEnrich";

interface NewInRoleTriggerSectionProps {
	formValues: Partial<CreateCompanyEnrich>;
	setValue: UseFormSetValue<CreateCompanyEnrich>;
}

export const NewInRoleTriggerSection: FunctionComponent<NewInRoleTriggerSectionProps> = ({
	formValues,
	setValue,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`companyEnrich.${key}`);

	const newInRole = formValues.newInRole ?? {};
	const changeTypes = newInRole?.changeTypes ?? [];
	const preset = newInRole?.window?.preset;

	const updateNewInRole = (next: NonNullable<NewInRole>) => {
		setValue("newInRole", next, { shouldDirty: true, shouldValidate: true });
	};

	const toggleChangeType = (value: NewInRoleChangeType) => {
		const nextChangeTypes = changeTypes.includes(value)
			? changeTypes.filter((item) => item !== value)
			: [...changeTypes, value];

		updateNewInRole({ ...newInRole, changeTypes: nextChangeTypes });
	};

	const handlePresetChange = (value: NewInRoleWindowPreset | null) => {
		if (!value) {
			updateNewInRole({ ...newInRole, window: null });

			return;
		}

		updateNewInRole({
			...newInRole,
			window:
				value === "custom"
					? { preset: value, fromDaysAgo: null, toDaysAgo: null }
					: { preset: value },
		});
	};

	const handleCustomChange = (key: "fromDaysAgo" | "toDaysAgo", raw: string) => {
		const parsed = raw === "" ? null : Number(raw);

		updateNewInRole({
			...newInRole,
			window: { ...(newInRole?.window ?? {}), preset: "custom", [key]: parsed },
		});
	};

	return (
		<div className="p-4 mb-4 rounded-xl border border-border-light bg-white">
			<div className="text-smd text-text font-bold mb-3">{ts("newInRoleFindPeopleThat")}</div>
			<div className="flex flex-col gap-3 mb-4">
				{NEW_IN_ROLE_CHANGE_TYPES.map(({ value, labelKey, infoKey }) => (
					<Checkbox
						key={value}
						isChecked={changeTypes.includes(value)}
						testId={`new-in-role-${value}`}
						onChange={() => toggleChangeType(value)}
					>
						<span className="inline-flex items-center gap-1.5">
							{ts(labelKey)}
							{infoKey && <Information information={ts(infoKey)} />}
						</span>
					</Checkbox>
				))}
			</div>

			<SelectField
				hideEmptyValue
				handleSelect={(value) => handlePresetChange(value as NewInRoleWindowPreset | null)}
				label={ts("newInRoleInTheLast")}
				name="newInRoleWindow"
				options={NEW_IN_ROLE_WINDOW_PRESETS.map(({ value, labelKey }) => ({
					value,
					title: ts(labelKey),
				}))}
				selectedOption={preset ?? null}
				testId="select-new-in-role-window"
			/>

			{preset === "custom" && (
				<div className="grid grid-cols-2 gap-4 mt-4">
					<InputField
						handleChange={(event: ChangeEvent<HTMLInputElement>) =>
							handleCustomChange("fromDaysAgo", event.target.value)
						}
						label={ts("newInRoleFromDaysAgo")}
						name="newInRoleFromDaysAgo"
						placeholder={ts("newInRoleDaysAgoPlaceholder")}
						type="number"
						value={newInRole?.window?.fromDaysAgo ?? ""}
					/>
					<InputField
						handleChange={(event: ChangeEvent<HTMLInputElement>) =>
							handleCustomChange("toDaysAgo", event.target.value)
						}
						label={ts("newInRoleToDaysAgo")}
						name="newInRoleToDaysAgo"
						placeholder={ts("newInRoleDaysAgoPlaceholder")}
						type="number"
						value={newInRole?.window?.toDaysAgo ?? ""}
					/>
				</div>
			)}
		</div>
	);
};
